import { useEffect, useMemo, useState } from "react";
import { formatMonthlyPrice } from "../../utils/priceFormat";
import "./CreatorTierAccordion.css";

const ACCENT = ["bronze", "gold", "burgundy"];

function CheckIcon({ className }) {
  return (
    <svg className={className} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Chevron({ open }) {
  return (
    <svg className={`cp-ta-chevron${open ? " is-open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function perkLines(tiers, index) {
  const tier = tiers[index];
  const own = (tier.perks || []).filter((p) => !/^everything in\b/i.test(String(p)));
  if (index === 0) return own;
  const prev = tiers[index - 1]?.name || "the previous tier";
  return [`Everything in ${prev}, plus:`, ...own];
}

/**
 * Mobile: independent toggles (multi-open). Single tier starts open.
 * Desktop (≥960px): exclusive accordion — always exactly one open.
 */
export default function CreatorTierAccordion({
  tiers = [],
  creatorFirstName = "this creator",
  onSubscribe,
}) {
  const list = useMemo(() => tiers.slice(0, 3), [tiers]);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 960px)").matches
  );
  const [openSet, setOpenSet] = useState(() => new Set(list.length === 1 ? [0] : []));
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 960px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (list.length === 1) {
      setOpenSet(new Set([0]));
      setOpenIndex(0);
    } else if (!isDesktop) {
      setOpenSet(new Set());
    } else {
      setOpenIndex(0);
    }
  }, [list.length, isDesktop]);

  if (!list.length) return null;

  const isOpen = (i) => (isDesktop ? openIndex === i : openSet.has(i));

  const handleHeaderClick = (i) => {
    if (isDesktop) {
      // Exclusive: open another tier; clicking the open one does nothing
      if (i !== openIndex) setOpenIndex(i);
      return;
    }
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <div className={`cp-tier-accordion${isDesktop ? " cp-tier-accordion--desktop" : " cp-tier-accordion--mobile"}`}>
      <div className="cp-ta-eyebrow">Support {creatorFirstName}</div>
      <h3 className="cp-ta-title">Pick a <em>circle</em></h3>
      <p className="cp-ta-sub">Ongoing access & perks — cancel anytime.</p>

      <div className="cp-ta-list">
        {list.map((tier, i) => {
          const open = isOpen(i);
          const accent = ACCENT[i] || "bronze";
          const perks = perkLines(list, i);
          return (
            <div
              key={tier.key}
              className={`cp-ta-row accent-${accent}${open ? " is-open" : ""}`}
              data-tier-idx={i}
            >
              <button
                type="button"
                className="cp-ta-header"
                onClick={() => handleHeaderClick(i)}
                aria-expanded={open}
              >
                <div className="cp-ta-header-main">
                  <span className="cp-ta-name">{tier.name}</span>
                  <span className="cp-ta-price">{formatMonthlyPrice(tier.price)}</span>
                </div>
                <Chevron open={open} />
              </button>

              {open && (
                <div className="cp-ta-body">
                  {tier.description && <p className="cp-ta-desc">{tier.description}</p>}
                  <div className="cp-ta-perks">
                    {perks.map((perk) => (
                      <div
                        key={`${tier.key}-${perk}`}
                        className={`cp-ta-perk${/^everything in\b/i.test(perk) ? " cp-ta-perk--include" : ""}`}
                      >
                        <CheckIcon />
                        <span>{perk}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="cp-ta-subscribe"
                    onClick={() => onSubscribe?.(tier, i)}
                  >
                    Subscribe now
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
